import React, { useState } from "react";
import { Menu, MenuProps } from "antd";
import {
  ContactsOutlined,
  ContainerOutlined,
  HomeOutlined,
  MobileOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import Title from "antd/es/typography/Title";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

type MenuItem = Required<MenuProps>["items"][number];

interface LevelKeysProps {
  key: string;
  children?: LevelKeysProps[];
}

const getKeyMapping = (menuItems: LevelKeysProps[]) => {
  const key: Record<string, number> = {};
  const parentMap: Record<string, string[]> = {};

  const processItems = (
    items2: LevelKeysProps[],
    level = 1,
    parent: string[] = [],
  ) => {
    items2.forEach((item) => {
      if (item.key) {
        key[item.key] = level;
        parentMap[item.key] = [...parent];
      }
      if (item.children) {
        processItems(item.children, level + 1, [...parent, item.key || ""]);
      }
    });
  };
  processItems(menuItems);
  return { key, parentMap };
};

const SidebarMenu: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const menuItems: MenuItem[] = [
    {
      key: "/portal",
      icon: <HomeOutlined />,
      label: t("sidebar.home"),
    },
    {
      key: "/portal/resources",
      icon: <ContainerOutlined />,
      label: t("sidebar.resources"),
    },
    {
      key: "/portal/devices",
      icon: <MobileOutlined />,
      label: t("sidebar.devices"),
    },
    {
      key: "/portal/users",
      icon: <TeamOutlined />,
      label: t("sidebar.users"),
    },
    {
      key: "/portal/service-accounts",
      icon: <ContactsOutlined />,
      label: t("sidebar.service-accounts"),
    },
  ];

  const { key: levelKeys, parentMap } = getKeyMapping(
    menuItems as LevelKeysProps[],
  );

  const getInitialOpenKeys = () => {
    const currentPath = window.location.pathname;
    const parentKeys = parentMap[currentPath] || [];
    return [...parentKeys, currentPath];
  };

  const [stateOpenKeys, setStateOpenKeys] =
    useState<string[]>(getInitialOpenKeys);

  const onOpenChange: MenuProps["onOpenChange"] = (openKeys) => {
    const currentOpenKey = openKeys.find(
      (key) => stateOpenKeys.indexOf(key) === -1,
    );
    if (currentOpenKey !== undefined) {
      const repeatIndex = openKeys
        .filter((key) => key !== currentOpenKey)
        .findIndex((key) => levelKeys[key] === levelKeys[currentOpenKey]);

      setStateOpenKeys(
        openKeys
          .filter((_, index) => index !== repeatIndex)
          .filter((key) => levelKeys[key] <= levelKeys[currentOpenKey]),
      );
    } else {
      setStateOpenKeys(openKeys);
    }
  };

  const onClick: MenuProps["onClick"] = (e) => {
    navigate(`${e.key}`);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
      }}
    >
      <Title level={3} style={{ marginLeft: "2rem", marginTop: "0.8rem" }}>
        {t("MeetingSpace")}
      </Title>
      <Menu
        mode="inline"
        selectedKeys={[window.location.pathname]}
        openKeys={stateOpenKeys}
        onOpenChange={onOpenChange}
        onClick={onClick}
        style={{ width: "15rem" }}
        items={menuItems}
      />
    </div>
  );
};

export default SidebarMenu;
